import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import ToolTip from 'core/components/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  VisitNoteSummaryDTO,
} from 'dtos';
import {
  formatDate,
} from 'utils/date';
import {
  getsSaltMenuItemDisabledTooltip,
  isSaltContentAvailable,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/helper';
import {
  notEmptyString,
} from '@populateui/utils';

export type SaltType =
'master'
| 'subjective'
| 'objective'
| 'plan'
| 'radiology'
| 'assessmentCodes'
| 'additionalAssessment'
| 'billingCodes'
| 'customServiceCodes'
| 'unknown';

type Props = {
  pastVisitNotes: VisitNoteSummaryDTO[];
  saltType: SaltType;
  anchorElement: HTMLElement | null;
  onClose: () => void;
  onClickMenuItem: (visitNoteId: number | null) => void;
};

type MenuItemProps = {
  disabledTooltip?: string;
  onClick: (visitNoteId: number | null) => void;
} & VisitNoteSummaryDTO;

function SaltMenuItem({
  disabledTooltip,
  visitNoteId,
  reasonForVisit,
  visitDateTime,
  onClick,
}: MenuItemProps) {
  const menuItemRef = useRef<HTMLDivElement>(null);
  const isDisabled = notEmptyString(disabledTooltip);

  return (
    <ToolTip
      ref={menuItemRef}
      auto={!isDisabled}
      placement="right"
      popperClassName="salt-content"
      title={isDisabled ? disabledTooltip : reasonForVisit}
    >
      <div className={clsx(
        '!text-xs !border-b !border-b-gray-200 !border-solid last:!border-b-0',
        { '!cursor-not-allowed': isDisabled },
      )}
      >
        <MenuItem
          disabled={isDisabled}
          onClick={() => {
            onClick(visitNoteId ?? null);
          }}
        >
          <div className="!w-[180px]">
            <div
              ref={menuItemRef}
              className="text-xs font-semibold text-slate-900 truncate"
            >
              {reasonForVisit}
            </div>
            <div className="text-xs font-medium text-slate-500 mt-1">
              {formatDate(visitDateTime ?? '')}
            </div>
          </div>
        </MenuItem>
      </div>
    </ToolTip>
  );
}

export default function SaltMenu({
  pastVisitNotes,
  saltType,
  anchorElement,
  onClose,
  onClickMenuItem,
}: Props) {
  const [dropdownElement, setDropupElement] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(dropdownElement);

  useEffect(() => {
    setDropupElement(anchorElement);
  }, [anchorElement]);

  return (
    <Menu
      elevation={1}
      anchorEl={dropdownElement}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      open={isMenuOpen}
      className="max-h-64"
      disablePortal
    >
      {pastVisitNotes?.map((visitNote) => (
        <SaltMenuItem
          {...visitNote}
          key={visitNote.visitNoteId}
          disabledTooltip={
            isSaltContentAvailable(visitNote, saltType)
              ? ''
              : getsSaltMenuItemDisabledTooltip(saltType)
          }
          onClick={onClickMenuItem}
        />
      ))}
    </Menu>
  );
}
